import React, { useCallback, useEffect, useState } from "react";
import { api, asArray } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, Plus, Save, Trash2, Star } from "lucide-react";

const empty = () => ({
    size: "", name: "", subtitle: "", price: "", per_meal: "",
    highlight: false, badge: "", includes: "", is_active: true, order: 0,
});

/**
 * MarFit packs manager. Admin CRUD for pricing packs shown on /marmitas.
 * Uses `/admin/marfit-packs` for full listing, `/marfit-packs` public.
 */
const PacksAdmin = () => {
    const [packs, setPacks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/marfit-packs");
            setPacks(asArray(data));
        } catch (err) {
            const detail = err?.response?.data?.detail || err?.message || "Erro";
            toast.error(`Erro ao carregar packs: ${detail}`);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const openNew = () => {
        setEditing(null);
        setForm(empty());
        setDialogOpen(true);
    };

    const openEdit = (pack) => {
        setEditing(pack);
        setForm({
            size: pack.size, name: pack.name, subtitle: pack.subtitle,
            price: String(pack.price), per_meal: String(pack.per_meal),
            highlight: pack.highlight, badge: pack.badge,
            includes: (pack.includes || []).join("\n"),
            is_active: pack.is_active, order: pack.order,
        });
        setDialogOpen(true);
    };

    const handleSave = async () => {
        // basic validation
        if (!form.size.trim() || !form.name.trim()) {
            toast.error("Tamanho e nome são obrigatórios.");
            return;
        }
        const priceNum = parseFloat(form.price);
        const perMealNum = parseFloat(form.per_meal);
        if (Number.isNaN(priceNum) || priceNum < 0) {
            toast.error("Preço inválido.");
            return;
        }
        if (Number.isNaN(perMealNum) || perMealNum < 0) {
            toast.error("Preço por marmita inválido.");
            return;
        }
        const payload = {
            size: form.size.trim(),
            name: form.name.trim(),
            subtitle: form.subtitle.trim(),
            price: priceNum,
            per_meal: perMealNum,
            highlight: !!form.highlight,
            badge: form.badge.trim(),
            includes: form.includes.split("\n").map((s) => s.trim()).filter(Boolean),
            is_active: !!form.is_active,
            order: parseInt(form.order, 10) || 0,
        };
        setSaving(true);
        try {
            if (editing) {
                await api.patch(`/admin/marfit-packs/${editing.id}`, payload);
                toast.success("Pack atualizado.");
            } else {
                await api.post("/admin/marfit-packs", payload);
                toast.success("Pack criado.");
            }
            setDialogOpen(false);
            load();
        } catch (err) {
            const detail = err?.response?.data?.detail || err?.message || "Erro";
            toast.error(`Erro a guardar: ${detail}`);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (pack) => {
        if (!window.confirm(`Remover o pack "${pack.name}"? Esta ação é irreversível.`)) return;
        try {
            await api.delete(`/admin/marfit-packs/${pack.id}`);
            toast.success("Pack removido.");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erro a remover.");
        }
    };

    return (
        <div className="space-y-4" data-testid="packs-admin">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="font-serif text-2xl text-stone-900">Packs MarFit Meals</h3>
                    <p className="text-sm text-stone-500 mt-1">
                        Preços e composição dos planos mostrados na landing /marmitas.
                    </p>
                </div>
                <Button
                    onClick={openNew}
                    className="bg-stone-900 hover:bg-stone-800 text-white"
                    data-testid="packs-add-btn"
                >
                    <Plus className="h-4 w-4 mr-2" /> Novo pack
                </Button>
            </div>

            {loading ? (
                <div className="flex items-center gap-2 text-stone-500 py-10 justify-center">
                    <Loader2 className="h-4 w-4 animate-spin" /> A carregar…
                </div>
            ) : packs.length === 0 ? (
                <div className="bg-stone-50 ring-1 ring-dashed ring-stone-300 rounded-xl py-10 text-center">
                    <p className="text-stone-600 text-sm">Ainda não existem packs configurados.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {packs.map((p) => (
                        <div
                            key={p.id}
                            className={`relative rounded-2xl p-5 ring-1 ${
                                p.is_active ? "ring-stone-200 bg-white" : "ring-stone-200/60 bg-stone-50 opacity-70"
                            }`}
                            data-testid={`admin-pack-${p.size}`}
                        >
                            {p.highlight && (
                                <div className="absolute -top-2 -right-2 bg-amber-400 text-amber-950 rounded-full w-8 h-8 flex items-center justify-center shadow" title="Destacado">
                                    <Star className="h-4 w-4" fill="currentColor" />
                                </div>
                            )}
                            <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 mb-1">
                                {p.name}
                            </div>
                            <div className="font-serif text-2xl text-stone-900">
                                {p.size} <span className="text-xs text-stone-500">marmitas</span>
                            </div>
                            <div className="text-xs text-stone-500 mb-3">{p.subtitle || "—"}</div>
                            <div className="mb-3">
                                <span className="font-serif text-2xl text-stone-900">€{Number(p.price).toFixed(0)}</span>
                                <span className="text-xs text-stone-500 ml-2">≈ €{Number(p.per_meal).toFixed(2)}/un.</span>
                            </div>
                            {p.badge && (
                                <div className="text-[10px] uppercase tracking-[0.16em] text-rose-700 bg-rose-50 rounded-full inline-block px-2 py-0.5 mb-3">
                                    {p.badge}
                                </div>
                            )}
                            <ul className="text-xs text-stone-600 space-y-1 mb-4">
                                {(p.includes || []).slice(0, 4).map((inc) => (
                                    <li key={inc} className="truncate">• {inc}</li>
                                ))}
                            </ul>
                            <div className="flex items-center gap-2">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openEdit(p)}
                                    className="flex-1"
                                    data-testid={`admin-pack-edit-${p.size}`}
                                >
                                    Editar
                                </Button>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleDelete(p)}
                                    className="text-rose-600 hover:bg-rose-50"
                                    data-testid={`admin-pack-delete-${p.size}`}
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                            {!p.is_active && (
                                <div className="mt-2 text-[10px] uppercase tracking-widest text-stone-500 text-center">
                                    Inativo — não aparece na landing
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="pack-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-xl">
                            {editing ? "Editar pack" : "Novo pack"}
                        </DialogTitle>
                        <DialogDescription>
                            Configura preço e composição. Os packs aparecem na landing MarFit por ordem crescente.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <FormField label="Nº de marmitas *">
                                <Input value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} placeholder="10" data-testid="pack-input-size" />
                            </FormField>
                            <FormField label="Nome do plano *">
                                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Rotina" data-testid="pack-input-name" />
                            </FormField>
                        </div>
                        <FormField label="Subtítulo">
                            <Input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} placeholder="5 dias · 2 refeições" />
                        </FormField>
                        <div className="grid grid-cols-2 gap-3">
                            <FormField label="Preço total (€) *">
                                <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="pack-input-price" />
                            </FormField>
                            <FormField label="Preço por marmita (€)">
                                <Input type="number" step="0.01" value={form.per_meal} onChange={(e) => setForm({ ...form, per_meal: e.target.value })} data-testid="pack-input-permeal" />
                            </FormField>
                        </div>
                        <FormField label="Badge (opcional)">
                            <Input value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} placeholder="Mais escolhido" />
                        </FormField>
                        <FormField label="Inclui (uma linha por item)">
                            <Textarea
                                rows={4}
                                value={form.includes}
                                onChange={(e) => setForm({ ...form, includes: e.target.value })}
                                placeholder={"10 refeições completas\nEmenta personalizada\nEntrega semanal"}
                                data-testid="pack-input-includes"
                            />
                        </FormField>
                        <div className="grid grid-cols-2 gap-3">
                            <FormField label="Ordem">
                                <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} />
                            </FormField>
                            <div className="flex flex-col justify-end gap-3">
                                <label className="flex items-center gap-2 text-sm">
                                    <Switch checked={form.highlight} onCheckedChange={(v) => setForm({ ...form, highlight: v })} data-testid="pack-toggle-highlight" />
                                    <span>Destacado</span>
                                </label>
                                <label className="flex items-center gap-2 text-sm">
                                    <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="pack-toggle-active" />
                                    <span>Ativo (visível na landing)</span>
                                </label>
                            </div>
                        </div>

                        <div className="flex items-center justify-end gap-2 pt-2 border-t">
                            <Button variant="outline" onClick={() => setDialogOpen(false)}>
                                Cancelar
                            </Button>
                            <Button
                                onClick={handleSave}
                                disabled={saving}
                                className="bg-stone-900 hover:bg-stone-800 text-white"
                                data-testid="pack-save-btn"
                            >
                                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                                Guardar
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

const FormField = ({ label, children }) => (
    <div>
        <Label className="text-xs uppercase tracking-[0.14em] text-stone-500 mb-1.5 block">{label}</Label>
        {children}
    </div>
);

export default PacksAdmin;
